import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Grid, Popover, useMediaQuery } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/en';  // Ensure locale is available for formatting
import { useDispatch, useSelector } from 'react-redux';
import { updateSelectedDate } from '../../store/slice/Ticketing.slice';  // Adjust import as per your project structure
import { useTheme } from '@emotion/react';

const CalendarTicket = ({ isOpen, handleClose, Ref,setOpenNext }) => {
  const theme = useTheme();
  const { css } = useSelector((store) => store?.widgetSettings);
  const dateCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Date"
  )?.fields;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorOriginVert, setAnchorOriginVert] = useState('bottom'); // default
  const [transformOriginVert, setTransformOriginVert] = useState('top'); // default
  const { selectedDate, StdDates,languageWords } = useSelector((store) => store?.Ticketing);
  const daysOfWeek = [
    languageWords?.monday?.slice(0, 2),
    languageWords?.Tuesday?.slice(0, 2),
    languageWords?.Wednesday?.slice(0, 2),
    languageWords?.Thursday?.slice(0, 2),
    languageWords?.Friday?.slice(0, 2),
    languageWords?.Saturday?.slice(0, 2),
    languageWords?.Sunday?.slice(0, 2),
  ];
  const dispatch = useDispatch();
  const today = dayjs(); // Get the current date
  const [currentDate, setCurrentDate] = useState(today); // State to track the current month
  const [selectedDateState, setSelectedDateState] = useState(dayjs(selectedDate)); // State to track the selected date
  const allowedDates = StdDates?.map((date) => dayjs(date));
  const firstDayOfMonth = (currentDate.startOf("month").day() + 6) % 7;
  const shouldDisableDate = (date) => {
    return !allowedDates.some((allowedDate) => allowedDate?.isSame(date, "day"));
  };
  const getTranslatedMonth = () => {
    const monthName = currentDate?.format("MMMM");
    return languageWords?.[monthName] || monthName;
  };

  const isPastDay = (day) => currentDate.date(day).isBefore(today, 'day');
  const isBlockedDate = (day) => !allowedDates.some((allowedDate) => allowedDate?.isSame(currentDate.date(day), "day"));
  const isToday = (day) => today.isSame(currentDate.date(day), 'day');
  const isSelected = (day) => selectedDateState && selectedDateState.isSame(currentDate.date(day), 'day');

  useEffect(() => {
    setSelectedDateState(dayjs(selectedDate));
  }, [selectedDate]);

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const handleDateSelect = (day) => {
    if (!isPastDay(day) && !isBlockedDate(day)) {
      const newSelectedDate = currentDate.date(day);
      setSelectedDateState(newSelectedDate); // Set the selected date
      const formattedDate = newSelectedDate.format('YYYY-MM-DD');
      dispatch(updateSelectedDate(formattedDate)); // Dispatch selected date
      handleClose();
    //  setOpenNext(true)
    }
  };
 
  const anchorEl = Ref ;
useEffect(() => {
  if (!isOpen || !anchorEl) return;

  const rect = anchorEl.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const POPOVER_HEIGHT = 400;

  if (rect.bottom + POPOVER_HEIGHT > viewportHeight) {
    setAnchorOriginVert('top');
    setTransformOriginVert('bottom');
  } else {
    setAnchorOriginVert('bottom');
    setTransformOriginVert('top');
  }
}, [isOpen, anchorEl]);


  return (
    <Popover
    open={isOpen}
      anchorEl={anchorEl}
    onClose={handleClose}
    anchorOrigin={{
      vertical: anchorOriginVert,
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: transformOriginVert,
      horizontal: 'center',
    }}

    PaperProps={{
      sx: {
        width: isSmallScreen ? "90%" : isMediumScreen ? "80%" : "80%",
        maxWidth: "430px",
        borderRadius: 2,
        boxShadow: 3,
        p: 2,
      },
    }}
    >
      <Box
        sx={{
          textAlign: 'center',
          backgroundColor: theme?.calender?.bgColor,
          px: 2,
          paddingBottom: 2,
        }}
      >
        {/* Header with arrows and year/month */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
          <IconButton onClick={handlePrevMonth} 
             sx={{
              color: dateCss?.CalendarArrowColor
              , // Default color for icon
              "&:hover": {
                color: dateCss?.CalendarArrowColorHover, // Change color on hover
              },
              "&:active": {
                color: dateCss?.CalendarArrowColorActive, // Change color when clicked (active)
              },
              "&.Mui-disabled": {
                color: theme?.calender?.disabledArrowColor, // Change color when disabled
              },
            }}
      >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: "bold",color:dateCss?.
              CalendarYearTextColor }}>
            {currentDate.format("YYYY")}
          </Typography>
          <IconButton onClick={handleNextMonth}
                sx={{
                  color: dateCss?.CalendarArrowColor
                  , // Default color for icon
                  "&:hover": {
                    color: dateCss?.CalendarArrowColorHover, // Change color on hover
                  },
                  "&:active": {
                    color: dateCss?.CalendarArrowColorActive, // Change color when clicked (active)
                  },
                  "&.Mui-disabled": {
                    color: theme?.calender?.disabledArrowColor, // Change color when disabled
                  },
                }}
           >
            <ArrowForward />
          </IconButton>
        </Box>

        {/* Display Year and Month */}
        <Typography
          sx={{
            marginBottom: 1,
            color: dateCss?.CalendarMonthTextColor,
            fontSize: '24px',
            fontWeight: 'bold',
          }}
        >
          {getTranslatedMonth()}
        </Typography>

        {/* Days of the week */}
        <Grid container spacing={1} mt={3}>
          
          {daysOfWeek.map((day, index) => (
            <Grid item xs={1.71} key={index}>
              <Typography variant="body2" sx={{
                 fontWeight: "bold",
                 color: dateCss?.CalendarDaysTextColor,
                 fontSize: isSmallScreen ? "0.8rem" : "1rem",
                 textAlign: "center", 
              }}>
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Dates Grid */}
        <Grid container spacing={1} sx={{ marginTop: 1 }}>
        {[...Array(firstDayOfMonth)].map((_, i) => (
              <Grid item xs={1.71} key={`empty-${i}`} />
            ))}

          {/* Render all days of the month */}
          {[...Array(currentDate.daysInMonth())].map((_, dayIndex) => {
            const day = dayIndex + 1;
            const isBlocked = isBlockedDate(day);
            const isSelectedDay = isSelected(day);
            const isActive = !isPastDay(day) && !isBlocked;

            return (
              <Grid item xs={1.71} key={dayIndex}>
                <Box
                  onClick={() => isActive && handleDateSelect(day)} // Only allow selection if active
                  sx={{
                    
                    border: '2px dotted',
                    borderColor: isBlocked || isPastDay(day)
                      ? dateCss?.CalendarDateInactiveBorderColor
                       : isSelectedDay && isActive
                      ? theme?.calender?.dateTextColorActive
                      : dateCss?.CalendarDateBorderColor,
                    color: isSelectedDay
                      ? dateCss?.CalendarDateTextColorActive
                      : isBlocked || isPastDay(day)
                      ? dateCss?.CalendarDateInactiveTextColor  
                      :  dateCss?.CalendarDateTextColor,
                      backgroundColor: isSelectedDay && isActive ? dateCss?.CalendarDateBackgroundColorActive: !isActive ? dateCss?.
                      CalendarDateInactiveBackgroundColor : dateCss?.
                      CalendarDateBackgroundColor ,                           
                    cursor: isActive ? 'pointer' : 'default',
                    borderRadius: '8px',
                    padding: 1,
                    textAlign: 'center',
                    transition: 'background-color 0.2s ease',
                    width: isSmallScreen ? "30px" : "40px",  
                    height: isSmallScreen ? "30px" : "40px",  
                    fontWeight: isActive ? 'bold' : 'normal',
                    lineHeight: '1',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover':{
                      backgroundColor: isActive && dateCss?.CalendarDateBackgroundColorHover,
                      color:dateCss?.CalendarDateTextColorHover
                      

                    }
                  }}
                >
                  <Typography
                    variant="body2"         
                    sx={{
                  
                      color: isSelectedDay
                        ? theme?.calender?.dateTextColorActive
                        : isBlocked || isPastDay(day)
                        ? theme?.widget?.disableTextColor ??  "#969BA0"
                        : isActive
                        ? theme?.calender?.dateTextColor
                        : theme?.calender?.dateTextColor,
                        fontSize: isSmallScreen ? "12px" : "16px",
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Popover>
  );
};

export default CalendarTicket;
